import { useEffect, useMemo, useRef, useState } from "react";
import {
    Accordion,
    Button,
    Card,
    Dropdown,
    Form,
    InputGroup,
    Spinner,
} from "react-bootstrap";
import {
    Map as MapLibreMap,
    Layer,
    Source,
    type MapLayerMouseEvent,
    type MapRef,
} from "react-map-gl/maplibre";
import type { FeatureCollection } from "geojson";
import { BBoxSelector } from "./BBoxSelector";
import type { Map } from "../lib/types";
import { CLIENT, downloadUrl } from "../lib/api";
import { bboxesFromLLUR } from "../lib/map/bbox";
import { getFramePieces, getPrice, getRenderPieces } from "../lib/pricing";
import { statusToColor, statusToIcon } from "../lib/status";
import { convertFromMM, estMeters, fmtDist, fmtLat, fmtLon } from "../lib/units";

interface Props {
    map: Map;
    card?: boolean;
    onChange?: (newMap: Map) => void;
    // When set, each rendered tile becomes a clickable cell on the overview
    // map; clicking one calls onTileClick with that tile's file key. The tile
    // whose key equals selectedKey is highlighted.
    selectedKey?: string | null;
    onTileClick?: (key: string) => void;
}

const dollarFmt = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});

export function MapOverview({
    map,
    card = false,
    onChange,
    selectedKey = null,
    onTileClick,
}: Props) {
    const [mapName, setMapName] = useState(map.friendlyName);
    const [hovering, setHovering] = useState(false);
    const mapRef = useRef<MapRef | null>(null);

    useEffect(() => {
        setMapName(map.friendlyName);
    }, [map.friendlyName]);

    // Tiles are clickable only once the map is rendered and only when a click
    // handler is supplied (e.g. on the checkout page, not the renders cards).
    const tilesClickable =
        !!onTileClick &&
        (map.status === "rendered" || map.status === "framed") &&
        getRenderPieces(map.files).length > 0;

    // Build one clickable cell per rendered tile. bboxesFromLLUR yields cells
    // in the same order the render pipeline emits tiles — columns west→east,
    // rows south→north — so cell i corresponds to the i-th render piece. See
    // subtile_iter in products/topograph/geometry/render.py.
    const tileFC = useMemo<FeatureCollection>(() => {
        const ll = map.extent?.ll;
        const ur = map.extent?.ur;
        if (!tilesClickable || !ll || !ur) {
            return { type: "FeatureCollection", features: [] };
        }
        const meters = estMeters(ll, ur);
        const heightMm = meters[0] ? (map.width * meters[1]) / meters[0] : map.width;
        const renderPieces = getRenderPieces(map.files);
        const fc = bboxesFromLLUR(
            { lng: ll.lng, lat: ll.lat },
            { lng: ur.lng, lat: ur.lat },
            map.width,
            heightMm,
        );
        return {
            type: "FeatureCollection",
            features: fc.features.map((f, i) => ({
                ...f,
                properties: { key: renderPieces[i] ?? null },
            })),
        };
    }, [tilesClickable, map.extent, map.width, map.files]);

    // A single-feature collection holding just the selected tile, used to paint
    // the selection highlight without regenerating the full grid.
    const selectedFC = useMemo<FeatureCollection>(() => {
        const feat = tileFC.features.find((f) => f.properties?.key === selectedKey);
        return { type: "FeatureCollection", features: feat ? [feat] : [] };
    }, [tileFC, selectedKey]);

    const onMapClick = (e: MapLayerMouseEvent) => {
        const key = e.features?.[0]?.properties?.key;
        if (typeof key === "string") onTileClick?.(key);
    };

    function rerender(m: Map) {
        CLIENT.createMap({
            extent: m.extent,
            width: m.width,
            maxX: m.maxX,
            maxZ: m.maxZ,
            buildingScale: m.buildingScale,
            friendlyName: m.friendlyName,
        }).then((x) => onChange?.(x));
    }

    function deleteMap(m: Map) {
        CLIENT.deleteMap(m.id).then(() => {
            onChange?.({ ...m, status: "deleted" });
        });
    }

    const ll = map.extent?.ll;
    const ur = map.extent?.ur;
    if (!ll || !ur) {
        return <div>Map has no extent.</div>;
    }

    const meters = estMeters(ll, ur);
    const heightMm = (map.width * meters[1]) / meters[0];

    // Fit bounds on map load with a small padding.
    const onLoad = () => {
        const m = mapRef.current;
        if (!m) return;
        const xSpan = (ur.lng - ll.lng) * 0.01;
        const ySpan = (ur.lat - ll.lat) * 0.01;
        m.fitBounds(
            [
                [ll.lng - xSpan, ll.lat - ySpan],
                [ur.lng + xSpan, ur.lat + ySpan],
            ],
            { padding: 10, duration: 0 },
        );
    };

    const header = (
        <InputGroup>
            <InputGroup.Text style={{ background: "none", border: "none", color: "#ccc" }}>
                {map.status === "processing" ? (
                    <Spinner animation="border" variant="warning" size="sm" />
                ) : (
                    <i
                        className={`bi bi-${statusToIcon(map.status)}`}
                        style={{ marginRight: 2, color: statusToColor(map.status) }}
                    />
                )}
                &nbsp;<i className="bi bi-map" />&nbsp;{map.id.slice(0, 7)}
            </InputGroup.Text>
            <Form.Control
                value={mapName}
                onChange={(e) => setMapName(e.currentTarget.value)}
                onBlur={() => {
                    if (mapName !== map.friendlyName) {
                        rerender({ ...map, friendlyName: mapName });
                    }
                }}
                style={{
                    background: "none",
                    color: "white",
                    marginLeft: 3,
                    borderColor: "rgb(255, 191, 0)",
                    borderWidth: 1,
                }}
            />
        </InputGroup>
    );

    const framePieces = getFramePieces(map.files);
    const renderPieces = getRenderPieces(map.files);
    const allPieces = [...framePieces.map((k) => ({ section: "Frame Segments", k })), ...renderPieces.map((k) => ({ section: "Map Tiles", k }))];

    const rendered = map.status === "rendered" || map.status === "framed";

    return (
        <>
            {card ? <Card.Header><Card.Title style={{ paddingTop: 10 }}>{header}</Card.Title></Card.Header> : header}

            <div style={{ height: 500, width: "100%", margin: 0 }}>
                <MapLibreMap
                    ref={mapRef}
                    initialViewState={{
                        longitude: (ll.lng + ur.lng) / 2,
                        latitude: (ll.lat + ur.lat) / 2,
                        zoom: 9,
                    }}
                    mapStyle="https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json"
                    interactive={false}
                    attributionControl={false}
                    onLoad={onLoad}
                    interactiveLayerIds={tilesClickable ? ["tg-tile-fill"] : undefined}
                    onClick={tilesClickable ? onMapClick : undefined}
                    onMouseEnter={tilesClickable ? () => setHovering(true) : undefined}
                    onMouseLeave={tilesClickable ? () => setHovering(false) : undefined}
                    cursor={hovering ? "pointer" : undefined}
                    style={{ width: "100%", height: "100%" }}
                >
                    {tilesClickable && (
                        <>
                            <Source id="tg-tiles" type="geojson" data={tileFC}>
                                <Layer
                                    id="tg-tile-fill"
                                    type="fill"
                                    paint={{ "fill-color": "#FFBF00", "fill-opacity": 0.06 }}
                                />
                            </Source>
                            <Source id="tg-tile-selected" type="geojson" data={selectedFC}>
                                <Layer
                                    id="tg-tile-selected-fill"
                                    type="fill"
                                    paint={{ "fill-color": "#FFBF00", "fill-opacity": 0.32 }}
                                />
                                <Layer
                                    id="tg-tile-selected-line"
                                    type="line"
                                    paint={{
                                        "line-color": "#FFBF00",
                                        "line-width": 3,
                                        "line-opacity": 0.9,
                                    }}
                                />
                            </Source>
                        </>
                    )}
                    <BBoxSelector
                        ll={ll}
                        ur={ur}
                        width={map.width}
                        height={heightMm}
                        interactive={false}
                    />
                </MapLibreMap>
            </div>
            <br />

            <table style={{ width: "100%", margin: "1rem 0" }}>
                <tbody>
                    <tr>
                        <td>Object Size</td>
                        <td>&nbsp;{convertFromMM("cm", map.width)} × {convertFromMM("cm", heightMm)}</td>
                    </tr>
                    <tr>
                        <td>Geographic Size</td>
                        <td>&nbsp;{fmtDist(meters[0])} × {fmtDist(meters[1])}</td>
                    </tr>
                    <tr>
                        <td>Lower Left</td>
                        <td>&nbsp;{fmtLon(ll.lng)}, {fmtLat(ll.lat)}</td>
                    </tr>
                    <tr>
                        <td>Upper Right</td>
                        <td>&nbsp;{fmtLon(ur.lng)}, {fmtLat(ur.lat)}</td>
                    </tr>
                </tbody>
            </table>
            <br />

            {!card && (
                <>
                    <Button variant="outline-danger" onClick={() => deleteMap(map)}>
                        <i className="bi bi-trash" /> Delete Map
                    </Button>{" "}
                    {!rendered && (
                        <Button variant="outline-light" onClick={() => rerender(map)}>
                            <i className="bi bi-arrow-counterclockwise" /> Rerender Map
                        </Button>
                    )}{" "}
                    <Button
                        variant="outline-info"
                        onClick={() => {
                            window.location.href = `/create?w=${ll.lng}&s=${ll.lat}&e=${ur.lng}&n=${ur.lat}&width=${map.width}&title=${encodeURIComponent(map.friendlyName)}&max_z=${map.maxZ}&building_scale=${map.buildingScale}`;
                        }}
                    >
                        <i className="bi bi-pencil" /> Edit Map
                    </Button>
                    <br /><br />
                </>
            )}

            <Accordion data-bs-theme="dark">
                <Accordion.Item eventKey="0">
                    <Accordion.Header>
                        {Object.keys(map.files).filter((k) => k.endsWith(".obj")).length} OBJ files
                    </Accordion.Header>
                    <Accordion.Body>
                        <small>
                            <ul>
                                <li>
                                    Building footprint and height data{" "}
                                    <a href="https://www.openstreetmap.org/copyright">copyright OpenStreetMap contributors.</a>
                                </li>
                                <li>
                                    Terrain data public domain courtesy of the{" "}
                                    <a href="https://eospso.nasa.gov/missions/shuttle-radar-topography-mission">Shuttle Radar Topography Mission (SRTM).</a>
                                </li>
                            </ul>
                            The full set of OBJ files (including snapfits and frame segments) is available after purchase.
                            Before then, you can view bare tiles and the completed frame only.
                            <br /><br />
                            Download licensed under{" "}
                            <a href="https://creativecommons.org/licenses/by-nc/4.0/deed.en">CC BY-NC 4.0</a>
                            &nbsp;for your enjoyment.
                            <hr />
                            <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                                <Dropdown style={{ flexGrow: 1 }}>
                                    <Dropdown.Toggle style={{ width: "100%" }}>
                                        Select file to download
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Header>Frame Segments</Dropdown.Header>
                                        {framePieces.length > 0 ? (
                                            framePieces.map((k) => (
                                                <Dropdown.Item key={k} href={downloadUrl(map.files[k], k)}><small>{k}</small></Dropdown.Item>
                                            ))
                                        ) : (
                                            <Dropdown.Item disabled>
                                                <small style={{ color: "goldenrod" }}>Purchase map to see frame segments.</small>
                                            </Dropdown.Item>
                                        )}
                                        <Dropdown.Header>Map Tiles</Dropdown.Header>
                                        {renderPieces.length > 0 ? (
                                            renderPieces.map((k) => (
                                                <Dropdown.Item key={k} href={downloadUrl(map.files[k], k)}><small>{k}</small></Dropdown.Item>
                                            ))
                                        ) : (
                                            <Dropdown.Item disabled>
                                                <small style={{ color: "goldenrod" }}>Map is still being created!</small>
                                            </Dropdown.Item>
                                        )}
                                    </Dropdown.Menu>
                                </Dropdown>
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        for (const { k } of allPieces) {
                                            window.open(downloadUrl(map.files[k], k));
                                        }
                                    }}
                                >
                                    <i className="bi bi-download" /> Download All
                                </Button>
                            </div>
                        </small>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            {card && (
                <Card.Footer>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <div>
                            <Button
                                variant="outline-danger"
                                disabled={map.status === "framed"}
                                onClick={() => deleteMap(map)}
                            >
                                <i className="bi bi-trash" />
                            </Button>{" "}
                            {!rendered && (
                                <Button variant="outline-light" onClick={() => rerender(map)}>
                                    <i className="bi bi-arrow-counterclockwise" />
                                </Button>
                            )}{" "}
                            <Button
                                variant="outline-info"
                                onClick={() => {
                                    window.location.href = `/create?w=${ll.lng}&s=${ll.lat}&e=${ur.lng}&n=${ur.lat}&width=${map.width}&title=${encodeURIComponent(map.friendlyName)}&max_z=${map.maxZ}&building_scale=${map.buildingScale}`;
                                }}
                            >
                                <i className="bi bi-pencil" />
                            </Button>
                        </div>
                        <div>
                            <Button
                                variant={rendered ? "outline-success" : "outline-danger"}
                                disabled={!rendered}
                                onClick={() => {
                                    window.location.href = `/checkout?mapId=${map.id}`;
                                }}
                            >
                                <i className="bi bi-bag" />{" "}
                                {rendered ? dollarFmt.format(getPrice(map)) : "???"}
                            </Button>
                        </div>
                    </div>
                </Card.Footer>
            )}
        </>
    );
}
